import { InfoOutline } from '@mui/icons-material';
import { TablePagination } from '@mui/material';
import { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ROUTE_AUDIT_CASE } from '@/constants/router';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useGetSelfAssessments } from '@/queries/get-audit-cases';
import type { AuditDraftStatus } from '@/types';
import { formatDateString } from '@/utils/date';
import { getTranslation } from '@/utils/translations';

import styles from './self-assessments-list.module.scss';

const LazyPendingSelfAssessmentModal = lazy(
  () => import('./pending-self-assessment-modal/pending-self-assessment-modal'),
);

interface TableSettings {
  page: number;
  pageSize: number;
}

interface SelfAssessmentsListProps {
  labels: Record<string, Record<string, string>>;
  supplierId: string;
}

export const SelfAssessmentsList = ({ labels, supplierId }: SelfAssessmentsListProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const handleErrors = useErrorHandling();

  const [tableSettings, setTableSettings] = useState<TableSettings>({
    page: 0,
    pageSize: 10,
  });
  const [openPendingModal, setOpenPendingModal] = useState(false);

  const {
    data: selfAssessments,
    isLoading,
    error,
  } = useGetSelfAssessments(
    supplierId,
    tableSettings.pageSize,
    tableSettings.page * tableSettings.pageSize,
  );

  useEffect(() => {
    if (error) {
      handleErrors(error);
    }
  }, [error]);

  const handleRowClick = (uuid: string, status: AuditDraftStatus) => {
    switch (status) {
      case 'OPEN':
      case 'REJECTED':
        navigate(ROUTE_AUDIT_CASE(uuid));
        break;
      case 'PENDING':
        setOpenPendingModal(true);
        break;
      default:
        break;
    }
  };

  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ) => {
    setTableSettings({ ...tableSettings, page: value });
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = Number(event.target.value);

    if (typeof selfAssessments?.total === 'number' && newPageSize > selfAssessments.total) {
      setTableSettings({ pageSize: newPageSize, page: 0 });
    } else {
      setTableSettings({ ...tableSettings, pageSize: newPageSize });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.idColumn}>{t('dashboard.drafts_list.id')}</th>
            <th>{t('dashboard.drafts_list.self_assessment')}</th>
            <th>{t('dashboard.drafts_list.status')}</th>
            <th>{t('dashboard.drafts_list.completed')}</th>
            <th>{t('dashboard.drafts_list.created_at')}</th>
            <th>{t('dashboard.drafts_list.updated_at')}</th>
          </tr>
        </thead>
        <tbody>
          {selfAssessments?.auditCases?.length ? (
            selfAssessments?.auditCases?.map((selfAssessment) => (
              <tr
                onClick={() => handleRowClick(selfAssessment.uuid, selfAssessment.status)}
                key={selfAssessment.uuid}
              >
                <td>{selfAssessment.uuid.split('-')[0]}</td>
                <td>
                  {selfAssessment.modules.map((module) => (
                    <span key={module.id}>
                      {getTranslation(labels[module.id], language)}
                      <br />
                    </span>
                  ))}
                </td>
                <td
                  className={`${styles.status} ${selfAssessment.status === 'ACCEPTED' && styles.submitted}`}
                >
                  {t(`status.${selfAssessment.status.toLowerCase()}`)}
                </td>
                <td>{selfAssessment.complete ? t('common.yes') : t('common.no')}</td>
                <td>{formatDateString(selfAssessment.createdAt)}</td>
                <td>{formatDateString(selfAssessment.updatedAt)}</td>
              </tr>
            ))
          ) : (
            <EmptyListPlaceholder />
          )}
        </tbody>
      </table>
      <TablePagination
        className={styles.pagination}
        component="div"
        count={selfAssessments?.total ?? 0}
        page={tableSettings.page}
        onPageChange={handlePageChange}
        rowsPerPage={tableSettings.pageSize}
        onRowsPerPageChange={handlePageSizeChange}
        labelRowsPerPage={t('dashboard.drafts_list.rows_per_page')}
      />

      {openPendingModal && (
        <LazyPendingSelfAssessmentModal onClose={() => setOpenPendingModal(false)} />
      )}
    </>
  );
};

const EmptyListPlaceholder = () => {
  const { t } = useTranslation();

  return (
    <tr>
      <td colSpan={6} className={styles.noSelfAssessments}>
        <div>
          <InfoOutline />
          <p>{t('dashboard.drafts_list.no_self_assessments')}</p>
        </div>
      </td>
    </tr>
  );
};
